import * as Sentry from "@sentry/react";
//eslint-disable-next-line import/no-unresolved

import {
  ACCOUNTS,
  ADD_COMPANY,
  BOOK_MEETING,
  ADD_CREDIT_LIMIT_ALLOCATION,
  ADD_CREDIT_LIMITS,
  ADD_PARTNERS,
  BALANCES_LINK,
  BILLINGS_LINK,
  COMPANIES,
  CONFIRM_OTP,
  CREDIT_LIMITS_LINK,
  DASHBOARD,
  EMAILS_LINK,
  FUNDER_LINK,
  FUNDER_MANAGEMENT_LINK,
  HEALTH_CHECK_LINK,
  INCOMLEND_INTERNAL_LINK,
  IT_LINK,
  LEADS,
  AGENT_DASHBOARD,
  AGENT_TERMSHEETS,
  AGENT_LEADS,
  LEADS_TABLE,
  LEGAL_LINK,
  MAPS,
  ORG_CHART_LINK,
  REGISTER_FOR_FINANCING,
  RISK_LINK,
  SOFR_LINK,
  SUPPORT,
  SUPPORT_EMAIL,
  TAX_INVOICES_LINK,
  TERMSHEETS_LINK,
  TR_LINK,
  USERS_LINK,
  WITHDRAWL_DEPOSIT_LINK,
  INVESTOR_DASHBOARD,
  INVESTOR_ALLOCATIONS,
  INVESTOR_FACTSHEET,
  INVESTOR_TRANSACTIONS,
  INVESTOR_MONTHLY_AUM,
  INVESTOR_GUIDES,
  INVESTOR_IMPORTER_REPAYMENT,
  INVESTOR_INVOICE_DETAILS,
  INVESTOR_ACTIVE_INVOICES_VISUALIZATION,
  AGENT_EWALLET,
  // AGENT_CREDIT_LIMITS_LINK,
} from "codes";
import { INVOICE_REQUEST_ROUTE } from "constants/invoice";

import "core-js/stable";
import {
  VITE_BUYER_PAGE,
  VITE_EXPERIMENTAL_RISK,
  VITE_INVOICES_PAGE,
} from "experimental_features";
import history from "helpers/history";
import { sanitizeLogText } from "helpers/frontendLogger";
import Auth from "layoutRoutes/LayoutAuth";
import IncomlendInternalLR from "layoutRoutes/LayoutIncomlendInternal";
import PresalesLR from "layoutRoutes/LayoutPresales";
import RegisterLR from "layoutRoutes/LayoutRegister";
import "react-app-polyfill/ie9";
import "react-app-polyfill/stable";
//eslint-disable-next-line import/no-unresolved
import "vite/modulepreload-polyfill";
import React, { lazy, Suspense } from "react";
import "normalize.css";
import DashboardInternal from "pages/dashboard-internal";
import Dashboard from "pages/dashboard";
import InvestorAccessPage from "pages/investor-access";
import SOFRPage from "pages/sofr-rates";
import "react-perfect-scrollbar/dist/css/styles.css";
import { prefetchCriticalRoutes } from "./utils/prefetchCriticalRoutes";
import { initCacheDebugger } from "./utils/cacheDebugger";

import type {} from "@mui/material/themeCssVarsAugmentation";
import Loadable from "components/Common/extended/Loadable";
// import CargoTracking from "pages/cargo-tracking";

import ErrorPage from "components/Common/ErrorPage";
import Loader from "components/Common/Loader";

import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import {
  createRoutesFromChildren,
  unstable_HistoryRouter as HistoryRouter,
  matchRoutes,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { PersistGate } from "redux-persist/integration/react";
import { persistor, store } from "redux/store";
import Root from "root";
import packageInfo from "../package.json";
import Incomlend from "./incomlend";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { INVESTOR_ACCESS_PATH } from "helpers/investorAccess";
import OriginationMember from "components/Dashboard/DepartmentScreen/OriginationMember";
import { notifyAppUpdate } from "services/appUpdate";

const { version } = packageInfo;

const sanitizeSentryUrl = (value: string): string => {
  try {
    const url = new URL(value, window.location.origin);
    return `${url.origin}${url.pathname}`;
  } catch {
    return value.split(/[?#]/, 1)[0];
  }
};

const sensitiveSentryFields = new Set([
  "access_key",
  "access_token",
  "api_key",
  "authorization",
  "auth_token",
  "cookie",
  "client_secret",
  "headers",
  "id_token",
  "mobile",
  "mobile_number",
  "otp",
  "password",
  "passwd",
  "private_key",
  "refresh_token",
  "secret",
  "secret_key",
  "set_cookie",
  "signing_key",
  "token",
  "user",
  "username",
  "verification_code",
]);

const sanitizeSentryValue = (
  value: unknown,
  fieldName = "",
  depth = 0,
): unknown => {
  if (depth > 12) return "[OMITTED]";

  const normalizedFieldName = fieldName
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase();
  if (sensitiveSentryFields.has(normalizedFieldName)) return undefined;

  if (typeof value === "string") {
    return /(?:url|uri|path)$/i.test(fieldName)
      ? sanitizeSentryUrl(value)
      : sanitizeLogText(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeSentryValue(item, "", depth + 1))
      .filter((item) => item !== undefined);
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).flatMap(([key, item]) => {
        const sanitized = sanitizeSentryValue(item, key, depth + 1);
        return sanitized === undefined ? [] : [[key, sanitized]];
      }),
    );
  }
  return value;
};

const scrubSentryEvent = <T extends Sentry.Event>(event: T): T => {
  if (event.request) {
    if (event.request.url) {
      event.request.url = sanitizeSentryUrl(event.request.url);
    }
    delete event.request.headers;
    delete event.request.cookies;
    delete event.request.data;
    delete event.request.query_string;
    event.request = sanitizeSentryValue(event.request) as typeof event.request;
  }
  delete event.user;
  if (event.message) {
    event.message = sanitizeLogText(event.message);
  }
  event.exception?.values?.forEach((exception) => {
    if (exception.value) {
      exception.value = sanitizeLogText(exception.value);
    }
  });
  if (event.transaction) {
    event.transaction = sanitizeSentryUrl(event.transaction);
  }
  if (event.extra) {
    event.extra = sanitizeSentryValue(event.extra) as typeof event.extra;
  }
  if (event.contexts) {
    event.contexts = sanitizeSentryValue(
      event.contexts,
    ) as typeof event.contexts;
  }
  if (event.tags) {
    event.tags = sanitizeSentryValue(event.tags) as typeof event.tags;
  }
  if (event.exception) {
    event.exception = sanitizeSentryValue(
      event.exception,
    ) as typeof event.exception;
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs
      .filter((breadcrumb) => breadcrumb.category !== "console")
      .map(
        (breadcrumb) => sanitizeSentryValue(breadcrumb) as Sentry.Breadcrumb,
      );
  }
  return event;
};

const scrubSentryBreadcrumb = (
  breadcrumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb | null => {
  // Raw console arguments can contain customer data; appLogger emits the safe,
  // structured error records that are needed for diagnostics.
  if (breadcrumb.category === "console") {
    return null;
  }
  return sanitizeSentryValue(breadcrumb) as Sentry.Breadcrumb;
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV,
  sendDefaultPii: false,
  tracesSampleRate: import.meta.env.VITE_ENV === "main" ? 1.0 : 0.25,
  tracePropagationTargets: ["localhost", /^https:\/\/incomlend\.com\/api/],
  enabled: import.meta.env.VITE_ENV === "main",
  release: version,
  beforeSend: scrubSentryEvent,
  beforeSendTransaction: scrubSentryEvent,
  beforeBreadcrumb: scrubSentryBreadcrumb,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
});

const { push, back } = history;

const Companies = Loadable(lazy(() => import("pages/company")));
const InvestorDashboardRouter = Loadable(
  lazy(() => import("investor/pages/InvestorDashboardRouter")),
);
const InvestorTransactions = Loadable(
  lazy(() => import("investor/pages/InvestorTransactions")),
);
const InvestorAllocations = Loadable(
  lazy(() => import("investor/pages/InvestorAllocations")),
);
const Deailfactsheat = Loadable(
  lazy(() => import("investor/pages/InvestorFactSheat")),
);
const InvestorMonthlyAUM = Loadable(
  lazy(() => import("investor/pages/MonthlyAUMPage")),
);
const InvertorImorterRepayment = Loadable(
  lazy(() => import("investor/pages/ImporterRepaymentPage")),
);
const ActiveInvoicesVisualization = Loadable(
  lazy(() => import("investor/pages/ActiveInvoicesVisualizationPage")),
);
const FundersSelection = Loadable(
  lazy(() => import("pages/funders-selection")),
);
const ReportsSelections = Loadable(
  lazy(() => import("pages/reports-selections")),
);

const InvoiceDetailsPage = Loadable(
  lazy(() => import("investor/pages/InvoiceDetailsPage")),
);

const SignupComponent = Loadable(lazy(() => import("pages/signup")));
const AdminAddedSignupComponent = Loadable(
  lazy(() => import("pages/signup/welcome")),
);
const MFA = Loadable(lazy(() => import("pages/thank-you")));
const Totp = Loadable(lazy(() => import("pages/twofa")));
const TradingRelationShips = Loadable(
  lazy(() => import("pages/trading-relationships")),
);
const PartnerProfile = Loadable(
  lazy(() => import("pages/trading-relationships/partner")),
);
const GeneralTermsCondition = Loadable(
  lazy(() => import("components/GTC/GeneralTermsConditions")),
);
const TermsOfUse = Loadable(lazy(() => import("pages/terms-of-use")));
const Privacy = Loadable(lazy(() => import("pages/privacy-policy")));
const Login = Loadable(lazy(() => import("pages/login")));
const RegisterCompany = Loadable(
  lazy(() => import("components/Company/Registration/RegisterCompany")),
);
const SelectProfile = Loadable(
  lazy(() => import("pages/company/add/register")),
);
const AddPartnerPage = Loadable(
  lazy(() => import("pages/company/add/partner")),
);
const CreditLimitPage = Loadable(
  lazy(() => import("pages/company/add/credit-limit")),
);
const CreditLimitAllocationPage = Loadable(
  lazy(() => import("pages/company/add/credit-limit-allocation")),
);
const KnowledgeCentre = Loadable(
  lazy(() => import("components/KnowledgeCentre")),
);
const AccountsListingPage = Loadable(
  lazy(() => import("pages/select-account")),
);
const Termsheets = Loadable(lazy(() => import("pages/termsheets")));
const Billings = Loadable(lazy(() => import("pages/billings")));
const Billing = Loadable(lazy(() => import("pages/billings/billing")));
const BookMeeting = Loadable(lazy(() => import("./components/BookMeeting")));
const CLRequest = Loadable(lazy(() => import("pages/cl-requests")));
const Settings = Loadable(lazy(() => import("pages/settings")));
const CreditLimitOpportunities = Loadable(
  lazy(() => import("pages/credit-limits-opportunities")),
);
const OpportunityPipelineStatus = Loadable(
  lazy(() => import("components/CreditCommittee/OpportunityPipeLine")),
);
const Emails = Loadable(lazy(() => import("pages/emails")));
const GuidePage = Loadable(lazy(() => import("pages/guides")));
const OrgChartPage = Loadable(lazy(() => import("pages/org-chart")));
const SupportPage = Loadable(lazy(() => import("pages/support")));
const IssuePage = Loadable(lazy(() => import("pages/support/issue")));
const Maps = Loadable(lazy(() => import("pages/maps")));
const Map = Loadable(lazy(() => import("pages/maps/map")));
const MapInstallmentPage = Loadable(
  lazy(() => import("pages/maps/map/MapInstallmentPage")),
);
const Funder = Loadable(lazy(() => import("pages/invoice-statuses")));
const InvoiceStatusPage = Loadable(
  lazy(() => import("pages/invoice-statuses/invoice-status")),
);
const Countries = Loadable(lazy(() => import("pages/countries")));
const ViewEditEligibility = Loadable(
  lazy(() => import("pages/countries/eligibility")),
);
const CreditLimits = Loadable(lazy(() => import("pages/credit-limits")));
const CLDetail = Loadable(lazy(() => import("pages/credit-limits/cl-detail")));
const OnboardingsPage = Loadable(
  lazy(() => import("pages/credit-limits/onboardings")),
);
const Accounts = Loadable(lazy(() => import("pages/accounts")));
const Account = Loadable(lazy(() => import("pages/accounts/account")));
const Leads = Loadable(lazy(() => import("pages/leads")));
const LeadAccount = Loadable(lazy(() => import("pages/leads/lead")));
const StatusesPage = Loadable(
  lazy(() => import("pages/credit-limits/statuses")),
);
const ITUserManagement = Loadable(lazy(() => import("pages/it/users")));
const ManageUserAccount = Loadable(lazy(() => import("pages/it/users/manage")));
const FunderManagement = Loadable(
  lazy(() => import("pages/it/funder-management")),
);
const FunderManagementDetail = Loadable(
  lazy(() => import("pages/it/funder-management/detail")),
);
const ManageUserGroups = Loadable(lazy(() => import("pages/it/groups")));
const ExperimentalFeatures = Loadable(
  lazy(() => import("pages/it/experimental-features")),
);
const ScriptsPage = Loadable(lazy(() => import("pages/it/scripts")));
const ConfigurationPage = Loadable(lazy(() => import("pages/configuration")));
const BalancesPage = Loadable(lazy(() => import("pages/balances")));
const WithdrawlDepositApprovalsPage = Loadable(
  lazy(() => import("pages/withdrawl-deposits")),
);
const HealthCheck = Loadable(lazy(() => import("components/HealthCheck")));
const TaxInvoicesPage = Loadable(lazy(() => import("pages/tax-invoices")));
const TaxInvoice = Loadable(lazy(() => import("pages/tax-invoices/invoice")));
const InvoiceOnboardingAccounts = Loadable(
  lazy(() => import("pages/invoice-onboarding/accounts")),
);
const AllInvoices = Loadable(lazy(() => import("pages/invoice-flow/invoices")));
const InvoiceDetails = Loadable(
  lazy(() => import("pages/invoice-onboarding/invocie-details/InvoiceDetails")),
);
const InvoiceOnboardingAccountInvoices = Loadable(
  lazy(() => import("pages/invoice-onboarding/accounts/invoices")),
);
const InvoiceOnboardingOverview = Loadable(
  lazy(
    () => import("pages/invoice-onboarding/invoice/InvoiceOnboardingOverview"),
  ),
);
const BuyerConfirmation = Loadable(
  lazy(() => import("components/Invoices/buyer-confirmation")),
);
const InvoiceDetailAdminNew = Loadable(
  lazy(
    () =>
      import(
        "pages/invoice-onboarding/invoice-allocation-new/InvoiceDetailAdminNew"
      ),
  ),
);
const InvoiceAllocationNew = Loadable(
  lazy(
    () =>
      import(
        "pages/invoice-onboarding/invoice-allocation-new/InvoiceAllocation"
      ),
  ),
);
const FunderAllocation = Loadable(
  lazy(
    () => import("pages/invoice-onboarding/funder-allocation/FunderAllocation"),
  ),
);
const AllocationDetailPage = Loadable(
  lazy(
    () =>
      import(
        "pages/invoice-onboarding/invoice-allocation-new/AllocationDetailPage"
      ),
  ),
);
const AllocationDecisionPage = Loadable(
  lazy(
    () =>
      import(
        "pages/invoice-onboarding/allocation-decision/AllocationDecisionPage"
      ),
  ),
);
const SendNoa = Loadable(
  lazy(() => import("components/Invoices/AddInvoice/NoaAndSla/index_noa")),
);
const SendSla = Loadable(
  lazy(() => import("components/Invoices/AddInvoice/NoaAndSla/index_sla")),
);
const FinancesDetails = Loadable(
  lazy(() => import("pages/invoice-onboarding/finances")),
);
const InvoiceFinancingTransactions = Loadable(
  lazy(() => import("pages/invoice-onboarding/transactions")),
);
const CreateTransactionPage = Loadable(
  lazy(
    () => import("pages/invoice-onboarding/transactions/create-transaction"),
  ),
);
const CreateDoubleEntryTransactionPage = Loadable(
  lazy(
    () => import("pages/invoice-onboarding/transactions/create-double-entry"),
  ),
);
const InvoiceFinancingEwallets = Loadable(
  lazy(() => import("pages/invoice-onboarding/ewallets")),
);
const CustomerTermSheet = Loadable(
  lazy(() => import("pages/termsheets/customer")),
);
const Buyer = Loadable(lazy(() => import("pages/buyer")));
const Invoices = Loadable(lazy(() => import("pages/invoices")));
const Invoice = Loadable(lazy(() => import("pages/invoices/invoice")));
const InvoiceEdition = Loadable(
  lazy(() => import("pages/invoices/invoice-edit")),
);
const ClientEwallet = Loadable(lazy(() => import("pages/client-ewallet")));
const AgentEwallet = Loadable(lazy(() => import("pages/agent-ewallet")));
const Ewallet = Loadable(lazy(() => import("pages/e-wallet")));
const Transaction = Loadable(lazy(() => import("pages/transaction")));
const Users = Loadable(lazy(() => import("pages/users")));
const UserProfile = Loadable(lazy(() => import("pages/users/profile")));
const BankAccountProfile = Loadable(
  lazy(() => import("pages/company/banks/profile")),
);
const LeadsTable = Loadable(lazy(() => import("pages/leads-table")));
const InvoiceBuybackDetailPage = Loadable(
  lazy(
    () =>
      import(
        "pages/invoice-onboarding/invoice-allocation-new/components/InvoiceDetailPage"
      ),
  ),
);

const Allocation = Loadable(
  lazy(() => import("pages/cl-requests/allocations")),
);
const Submission = Loadable(lazy(() => import("pages/submission")));

const PRELOAD_RECOVERY_KEY = "vite-preload-recovery-attempted";

window.addEventListener("vite:preloadError", (event) => {
  // Allow one cache/service-worker recovery per tab. If the asset still
  // cannot load after that reload, let Vite reject the import so the normal
  // error boundary can show its fallback instead of reloading forever.
  if (sessionStorage.getItem(PRELOAD_RECOVERY_KEY) === "true") {
    return;
  }

  sessionStorage.setItem(PRELOAD_RECOVERY_KEY, "true");

  // Vite rethrows preload failures unless this event is cancelled. Cancel
  // only the first failure because it is being handled by the recovery below.
  if (
    event &&
    typeof event === "object" &&
    "cancelable" in event &&
    event.cancelable &&
    "preventDefault" in event &&
    typeof event.preventDefault === "function"
  ) {
    event.preventDefault();
  }

  Promise.all([
    "serviceWorker" in navigator
      ? // eslint-disable-next-line compat/compat
        navigator.serviceWorker
          .getRegistrations()
          .then((registrations) =>
            Promise.all(
              registrations.map((registration) => registration.unregister()),
            ),
          )
      : Promise.resolve(),
    "caches" in window
      ? caches
          .keys()
          .then((cacheNames) =>
            Promise.all(
              cacheNames.map((cacheName) => caches.delete(cacheName)),
            ),
          )
      : Promise.resolve(),
  ]).finally(() => {
    window.location.reload();
  });
});

window.addEventListener("load", () => {
  prefetchCriticalRoutes();
});

root.render(
  // <CacheBuster isEnabled currentVersion={version} metaFileDirectory={"."}>
  <HelmetProvider>
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          {/* @ts-expect-error */}
          <HistoryRouter history={history}>
            <SentryRoutes>
              <Route element={<Root />}>
                <Route element={<Auth />}>
                  <Route path="/login" element={<Login />} />
                  <Route
                    path={INVESTOR_ACCESS_PATH}
                    element={<InvestorAccessPage />}
                  />
                  <Route path="/thank-you" element={<MFA />} />
                  <Route path={CONFIRM_OTP} element={<Totp />} />
                  <Route path="/signup" element={<SignupComponent />} />
                  <Route
                    path="/welcome"
                    element={<AdminAddedSignupComponent />}
                  />
                  <Route
                    path="/marketplace/gtc-suppliers"
                    element={<GeneralTermsCondition />}
                  />
                  <Route
                    path="/marketplace/gtc-buyers"
                    element={<GeneralTermsCondition />}
                  />
                  <Route path="/terms-of-use" element={<TermsOfUse />} />
                  <Route path="/privacy-policy" element={<Privacy />} />
                  {/* App-Incomlend */}
                  <Route
                    path="/dashboard/terms-of-use"
                    element={<TermsOfUse />}
                  />
                  <Route
                    path="/dashboard/privacy-policy"
                    element={<Privacy />}
                  />
                  {/* GTC links*/}
                  {/* <Route
                      path="/bank-fees-details"
                      element={<BankFeesDetails />}
                    />
                    <Route
                      path="/fees-and-charges"
                      element={<FeesAndCharges />}
                    />
                    <Route path="/gtc-buyers" element={<GTCBuyers />} />
                    <Route path="/gtc-funders" element={<GTCFunders />} />
                    <Route
                      path="/how-the-reservation-process-works"
                      element={<ReservationProcess />}
                    />
                    <Route
                      path="/incomlend-bank-account-details-amicorp"
                      element={<></>}
                    />
                    <Route
                      path="/incomlend-bank-account-details-buyers-trust"
                      element={<></>}
                    />
                    <Route
                      path="/incomlend-bank-account-details-buyers"
                      element={<></>}
                    />
                    <Route path="/privacy-policy-gtc" element={<></>} />
                    <Route path="/terms-and-conditions-gtc" element={<></>} /> */}
                  {/* end GTC*/}
                </Route>

                <Route element={<Incomlend />}>
                  <Route element={<RegisterLR />}>
                    <Route path={ADD_COMPANY} element={<RegisterCompany />} />
                  </Route>
                  <Route element={<PresalesLR />}>
                    <Route path="/" element={<Dashboard />} />
                    <Route
                      path={REGISTER_FOR_FINANCING}
                      element={<SelectProfile />}
                    />
                    {/* //Request from OPS on Feb 2024 to remove doc upload on presales */}
                    {/* <Route
                        path={UPLOAD_DOCUMENTS}
                        element={<UploadDocumentsPage />}
                      /> */}
                    <Route path={ADD_PARTNERS} element={<AddPartnerPage />} />
                    <Route
                      path={ADD_CREDIT_LIMITS}
                      element={<CreditLimitPage />}
                    />
                    <Route
                      path={ADD_CREDIT_LIMIT_ALLOCATION}
                      element={<CreditLimitAllocationPage />}
                    />
                  </Route>
                  <Route
                    path="/knowledge-centre"
                    element={<KnowledgeCentre />}
                  />
                  <Route path="/accounts" element={<AccountsListingPage />} />

                  {/* <Route path="/cargo-tracking" element={<CargoTracking />} /> */}
                  <Route path={`${TERMSHEETS_LINK}`} element={<Termsheets />} />

                  <Route path={`${BILLINGS_LINK}`} element={<Billings />} />
                  <Route path={`${BILLINGS_LINK}`} element={<Billings />} />

                  <Route path={`${BILLINGS_LINK}/:id`} element={<Billing />} />

                  <Route path={DASHBOARD} element={<Dashboard />} />
                  <Route path={COMPANIES} element={<Companies />} />
                  <Route path={BOOK_MEETING} element={<BookMeeting />} />

                  <Route path={TR_LINK} element={<TradingRelationShips />} />
                  <Route
                    path={`${TR_LINK}/profile/:id`}
                    element={<PartnerProfile />}
                  />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/submission" element={<Submission />} />
                  <Route
                    path={INCOMLEND_INTERNAL_LINK}
                    element={<IncomlendInternalLR />}
                  >
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}`}
                      element={<DashboardInternal />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/credit-limits-opportunities`}
                      element={<CreditLimitOpportunities />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/opportunities-pipeline`}
                      element={<OpportunityPipelineStatus />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${EMAILS_LINK}`}
                      element={<Emails />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${ORG_CHART_LINK}`}
                      element={<OrgChartPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${SUPPORT}`}
                      element={<SupportPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${SUPPORT}/:id`}
                      element={<IssuePage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${MAPS}`}
                      element={<Maps />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${MAPS}/:id`}
                      element={<Map />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${MAPS}/:id/installments`}
                      element={<MapInstallmentPage />}
                    />
                    {VITE_EXPERIMENTAL_RISK && (
                      <>
                        <Route
                          path={`${INCOMLEND_INTERNAL_LINK}${RISK_LINK}`}
                          element={<CLRequest />}
                        />
                        <Route
                          path={`${INCOMLEND_INTERNAL_LINK}${RISK_LINK}/:id/allocations`}
                          element={<Allocation />}
                        />
                      </>
                    )}

                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${FUNDER_LINK}`}
                      element={<Funder />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${FUNDER_LINK}/:id`}
                      element={<InvoiceStatusPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${LEGAL_LINK}`}
                      element={<Countries />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${LEGAL_LINK}/:iso3/view-eligibility`}
                      element={<ViewEditEligibility />}
                    />

                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${CREDIT_LIMITS_LINK}`}
                      element={<CreditLimits />}
                    />

                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${CREDIT_LIMITS_LINK}/:id`}
                      element={<CLDetail />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/onboardings`}
                      element={<OnboardingsPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${ACCOUNTS}`}
                      element={<Accounts />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${ACCOUNTS}/:id`}
                      element={<Account />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${LEADS}`}
                      element={<Leads />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${LEADS}/:id`}
                      element={<LeadAccount />}
                    />
                    {/* <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${CAMPAIGN_TESTING}`}
                      element={<TestCampaign />}
                    /> */}

                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/onboarding-statuses`}
                      element={<StatusesPage />}
                    />

                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${LEADS_TABLE}`}
                      element={<LeadsTable />}
                    />

                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${IT_LINK}/user-management`}
                      element={<ITUserManagement />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${IT_LINK}/user-management/:id/manage`}
                      element={<ManageUserAccount />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${IT_LINK}${FUNDER_MANAGEMENT_LINK}`}
                      element={<FunderManagement />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${IT_LINK}${FUNDER_MANAGEMENT_LINK}/:id`}
                      element={<FunderManagementDetail />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${IT_LINK}/groups`}
                      element={<ManageUserGroups />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${IT_LINK}/experimental-features`}
                      element={<ExperimentalFeatures />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${IT_LINK}/scripts`}
                      element={<ScriptsPage />}
                    />

                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/configuration`}
                      element={<ConfigurationPage />}
                    />

                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/onboarding-statuses`}
                      element={<StatusesPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${BALANCES_LINK}`}
                      element={<BalancesPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${WITHDRAWL_DEPOSIT_LINK}`}
                      element={<WithdrawlDepositApprovalsPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${WITHDRAWL_DEPOSIT_LINK}/new`}
                      element={<WithdrawlDepositApprovalsPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${WITHDRAWL_DEPOSIT_LINK}/:formId`}
                      element={<WithdrawlDepositApprovalsPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${SOFR_LINK}`}
                      element={<SOFRPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${HEALTH_CHECK_LINK}`}
                      element={<HealthCheck />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${TAX_INVOICES_LINK}`}
                      element={<TaxInvoicesPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${TAX_INVOICES_LINK}/:id`}
                      element={<TaxInvoice />}
                    />

                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${TERMSHEETS_LINK}`}
                      element={<Termsheets internalPage={true} />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${TERMSHEETS_LINK}/:id`}
                      element={<CustomerTermSheet internalPage={true} />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${BILLINGS_LINK}`}
                      element={<Billings />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}${BILLINGS_LINK}/:id`}
                      element={<Billing />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/accounts`}
                      element={<InvoiceOnboardingAccounts />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/invoices`}
                      element={<AllInvoices />}
                    />

                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/invoices/details`}
                      element={<InvoiceDetails />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/accounts/:id/invoices`}
                      element={<InvoiceOnboardingAccountInvoices />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/invoice/:id`}
                      element={<InvoiceOnboardingOverview />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/buyer-confirmation`}
                      element={<BuyerConfirmation />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-allocation-admin-new/:id`}
                      element={<InvoiceDetailAdminNew />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-allocation-new`}
                      element={<InvoiceAllocationNew />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-allocation-new/:id`}
                      element={<InvoiceDetailAdminNew />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/funder-allocation`}
                      element={<FunderAllocation />}
                    />

                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/funder-allocation/:id`}
                      element={<FunderAllocation />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/allocation-detail/:invoiceId/:allocationId`}
                      element={<AllocationDetailPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/allocation-decision/:id`}
                      element={<AllocationDecisionPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/invoice/noa`}
                      element={<SendNoa />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/invoice/${INVOICE_REQUEST_ROUTE.SLA}`}
                      element={<SendSla />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/finances`}
                      element={<FinancesDetails />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/transactions`}
                      element={<InvoiceFinancingTransactions />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/transactions/create-transaction`}
                      element={<CreateTransactionPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/transactions/create-double-entry`}
                      element={<CreateDoubleEntryTransactionPage />}
                    />
                    <Route
                      path={`${INCOMLEND_INTERNAL_LINK}/invoice-onboarding/ewallets`}
                      element={<InvoiceFinancingEwallets />}
                    />
                  </Route>
                  <Route
                    path={`${TERMSHEETS_LINK}/:id`}
                    element={<CustomerTermSheet />}
                  />
                  {VITE_BUYER_PAGE && (
                    <Route path="/buyer/:id" element={<Buyer />} />
                  )}
                  {VITE_INVOICES_PAGE && (
                    <>
                      <Route path="/invoices" element={<Invoices />} />
                      <Route path="/invoices/:id" element={<Invoice />} />
                      <Route
                        path="/client-ewallet"
                        element={<ClientEwallet />}
                      />
                      <Route
                        path="/invoices/edit/:id"
                        element={<InvoiceEdition />}
                      />
                    </>
                  )}
                  <>
                    <Route
                      path="/invoice-allocation-admin-new/:id"
                      element={<InvoiceDetailAdminNew />}
                    />
                    <Route
                      path="/invoice-onboarding/invoice-buyback-detail/:id"
                      element={<InvoiceBuybackDetailPage />}
                    />
                    <Route
                      path="/invoice-allocation-new"
                      element={<InvoiceAllocationNew />}
                    />
                    <Route
                      path="/invoice-allocation-new/:id"
                      element={<InvoiceDetailAdminNew />}
                    />
                    <Route
                      path="/funder-allocation/:id"
                      element={<FunderAllocation />}
                    />
                    <Route
                      path="/invoice-onboarding/allocation-detail/:invoiceId/:allocationId"
                      element={<AllocationDetailPage />}
                    />
                    <Route
                      path="/invoice-onboarding/allocation-decision/:id"
                      element={<AllocationDecisionPage />}
                    />
                    <Route
                      path="/invoice-onboarding/finances"
                      element={<FinancesDetails />}
                    />
                    <Route
                      path="/invoice-onboarding/transactions"
                      element={<InvoiceFinancingTransactions />}
                    />
                    <Route
                      path="/invoice-onboarding/transactions/create-transaction"
                      element={<CreateTransactionPage />}
                    />
                    <Route
                      path="/invoice-onboarding/transactions/create-double-entry"
                      element={<CreateDoubleEntryTransactionPage />}
                    />
                    <Route
                      path="/invoice-onboarding/ewallets"
                      element={<InvoiceFinancingEwallets />}
                    />
                  </>
                  <Route path="/e-wallet" element={<Ewallet />} />
                  <Route path="/transaction/:id" element={<Transaction />} />
                  <Route element={<Users />} path={USERS_LINK} />
                  <Route element={<UserProfile />} path={`${USERS_LINK}/:id`} />
                  <Route
                    element={<BankAccountProfile />}
                    path="/banks/:bankaccount"
                  />
                  <Route
                    path={INVESTOR_DASHBOARD}
                    element={<InvestorDashboardRouter />}
                  />
                  <Route
                    path={INVESTOR_ALLOCATIONS}
                    element={<InvestorAllocations />}
                  />
                  <Route
                    path={INVESTOR_FACTSHEET}
                    element={<Deailfactsheat />}
                  />
                  <Route
                    path={INVESTOR_TRANSACTIONS}
                    element={<InvestorTransactions />}
                  />
                  <Route
                    path={INVESTOR_MONTHLY_AUM}
                    element={<InvestorMonthlyAUM />}
                  />
                  <Route path="/investor/e-wallet" element={<Ewallet />} />
                  <Route path={INVESTOR_GUIDES} element={<GuidePage />} />
                  <Route
                    path={INVESTOR_IMPORTER_REPAYMENT}
                    element={<InvertorImorterRepayment />}
                  />
                  <Route
                    path={INVESTOR_ACTIVE_INVOICES_VISUALIZATION}
                    element={<ActiveInvoicesVisualization />}
                  />

                  <Route
                    path={INVESTOR_INVOICE_DETAILS}
                    element={<InvoiceDetailsPage />}
                  />

                  <Route
                    path={`${INVESTOR_INVOICE_DETAILS}/:invoiceId`}
                    element={<InvoiceDetailsPage />}
                  />

                  <Route
                    path="/funders-selection"
                    element={<FundersSelection />}
                  />

                  <Route
                    path="/reports-selections"
                    element={<ReportsSelections />}
                  />

                  <Route
                    path={AGENT_DASHBOARD}
                    element={<OriginationMember />}
                  />
                  <Route path={`${AGENT_LEADS}`} element={<Leads />} />
                  <Route
                    path={`${AGENT_TERMSHEETS}`}
                    element={<Termsheets internalPage={true} />}
                  />
                  <Route path={`${AGENT_EWALLET}`} element={<AgentEwallet />} />
                </Route>

                <Route
                  path="*"
                  element={
                    <Suspense fallback={<Loader open />}>
                      <ErrorPage
                        goBack={back}
                        push={push}
                        address={SUPPORT_EMAIL}
                      />
                    </Suspense>
                  }
                />
              </Route>
            </SentryRoutes>
          </HistoryRouter>
        </PersistGate>
      </Provider>
    </React.StrictMode>
  </HelmetProvider>,
  //  </CacheBuster>
);

"serviceWorker" in navigator &&
  serviceWorkerRegistration.register({
    onUpdate: (registration) => notifyAppUpdate({ registration }),
  });

// Initialize cache debugger in development
if (process.env.NODE_ENV === "dev") {
  initCacheDebugger();
}
